import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Label,
  Switch,
  Textarea,
  TimeDurationPicker,
} from '@ais-ui/ui-core';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useLocalization } from '@ais-ui/locale-management';
import { isNonEmpty } from '@ais-ui/utils';
import { LineDetectorFormValues } from './lineForm';
import { AngleInput } from '../../../../components/AngleInput';

interface LineAdvancedFormProps {
  form: UseFormReturn<LineDetectorFormValues>;
}

export const LineAdvancedForm = ({ form }: LineAdvancedFormProps) => {
  const { messages } = useLocalization();
  const [checkDirection, setCheckDirection] = React.useState(
    isNonEmpty(form.getValues('directionMinAngle')) ||
      isNonEmpty(form.getValues('directionMaxAngle'))
  );

  return (
    <div className="space-y-8">
      <Accordion type="single" collapsible>
        <AccordionItem value="advanced-text-item">
          <AccordionTrigger>Texts</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="activationText"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.eventdetectorsSaveAdvancedActivationTextLabel}
                      </FormLabel>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Textarea
                        id="eventDetectorAdvanced_activationTextField"
                        className="w-full"
                        placeholder={
                          messages.eventdetectorsSaveAdvancedActivationTextPlaceholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="deactivationText"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {
                          messages.eventdetectorsSaveAdvancedDeactivationTextLabel
                        }
                      </FormLabel>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Textarea
                        id="eventDetectorAdvanced_deactivationTextField"
                        className="w-full"
                        placeholder={
                          messages.eventdetectorsSaveAdvancedDeactivationTextPlaceholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-activation-times-item">
          <AccordionTrigger>Activation times</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name={'timeBeforeActivation'}
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex justify-between mr-2">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeActivationLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeActivationTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <TimeDurationPicker
                          value={field.value}
                          onChange={field.onChange}
                          format="HH:MM:SS"
                        ></TimeDurationPicker>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={'timeBeforeDeactivation'}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <div className="flex justify-between mr-2">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeDeactivationLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeDeactivationTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <TimeDurationPicker
                          value={field.value}
                          onChange={field.onChange}
                          format="HH:MM:SS"
                        ></TimeDurationPicker>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name={'timeBetweenTrigger'}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex justify-between mr-2">
                      <FormLabel>
                        {
                          messages.eventdetectorsSaveAdvancedTimeBetweenTriggerLabel
                        }
                      </FormLabel>
                      <InfoIconTooltip>
                        {
                          messages.eventdetectorsSaveAdvancedTimeBetweenTriggerTooltip
                        }
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <TimeDurationPicker
                        value={field.value}
                        onChange={field.onChange}
                        format="HH:MM:SS"
                      ></TimeDurationPicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'timeoutWhenActive'}
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between mr-2">
                      <FormLabel>
                        {
                          messages.eventdetectorsSaveAdvancedTimeoutWhenActivatedLabel
                        }
                      </FormLabel>
                      <InfoIconTooltip>
                        {
                          messages.eventdetectorsSaveAdvancedTimeoutWhenActivatedTooltip
                        }
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <TimeDurationPicker
                        value={field.value}
                        onChange={field.onChange}
                        format="HH:MM:SS"
                      ></TimeDurationPicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-direction-item">
          <AccordionTrigger>Direction</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6">
              <div className="flex flex-col space-y-4">
                <div className="flex justify-between mr-2">
                  <Label>
                    {messages.eventdetectorsSaveAdvancedCheckDirectionLabel}
                  </Label>
                  <InfoIconTooltip>
                    {
                      messages.eventdetectorsSaveAdvancedCheckDirectionTooltipTextTooltip
                    }
                  </InfoIconTooltip>
                </div>
                <Switch
                  id="eventDetectorAdvanced_checkDirection"
                  checked={checkDirection}
                  onCheckedChange={(checked) => {
                    setCheckDirection(checked);
                    form.setValue('directionMinAngle', undefined);
                    form.setValue('directionMaxAngle', undefined);
                  }}
                ></Switch>
              </div>
              {checkDirection && (
                <div className="flex justify-between space-x-4">
                  <FormField
                    control={form.control}
                    name="directionMinAngle"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <div className="flex justify-between mr-2">
                          <FormLabel>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMinAngleLabel
                            }
                          </FormLabel>
                          <InfoIconTooltip>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMinAngleTooltip
                            }
                          </InfoIconTooltip>
                        </div>
                        <FormControl onChange={() => form.clearErrors()}>
                          <AngleInput
                            placeholder={
                              messages.eventdetectorsSaveAdvancedDirectionMinAnglePlaceholder
                            }
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="directionMaxAngle"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <div className="flex justify-between mr-2">
                          <FormLabel>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMaxAngleLabel
                            }
                          </FormLabel>
                          <InfoIconTooltip>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMaxAngleTooltip
                            }
                          </InfoIconTooltip>
                        </div>
                        <FormControl onChange={() => form.clearErrors()}>
                          <AngleInput
                            placeholder={
                              messages.eventdetectorsSaveAdvancedDirectionMaxAnglePlaceholder
                            }
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-vessel-trigger-item">
          <AccordionTrigger>Vessel trigger</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="useVesselDimensions"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-4">
                      <div className="flex justify-between mr-4">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedUseVesselDimensionsLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedUseVesselDimensionsTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(value) => {
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-notifications-item">
          <AccordionTrigger>Notifications</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6">
              <div className="flex flex-col space-y-4">
                <Label>
                  {messages.eventdetectorsSaveAdvancedSendShipNotificationLabel}
                </Label>
                <div className="grid grid-cols-[auto_1fr] gap-4">
                  <FormField
                    control={form.control}
                    name="sendShipNotification"
                    render={({ field }) => (
                      <FormItem className="flex flex-col space-y-4">
                        <FormControl onChange={() => form.clearErrors()}>
                          <Switch
                            checked={field.value}
                            onCheckedChange={(value) => {
                              field.onChange(value);
                              form.setValue('shipNotificationText', '');
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {form.getValues('sendShipNotification') && (
                    <FormField
                      control={form.control}
                      name={'shipNotificationText'}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl onChange={() => form.clearErrors()}>
                            <Textarea
                              value={field.value}
                              onChange={field.onChange}
                            ></Textarea>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
